import { Link, createFileRoute } from '@tanstack/react-router';
import { ExternalLink, LogOut } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Field, NativeSelect } from '@/components/form';
import { toast } from '@/components/toast';
import { i18n, setLanguage, useT } from '@/lib/i18n';
import { EmptyState, PageHeader } from '@/components/page-header';
import { SettingsSection } from '@/components/settings-section';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { auth } from '@/lib/auth';
import { ROLE_LABELS, useClients, useIsDemo, useMe, useUpdateMe } from '@/lib/clients';
import { formatDateTime } from '@/lib/format';

export const Route = createFileRoute('/settings/profile')({ component: ProfileAndSecurity });

/** Field errors for the profile form (the API repeats them). */
export function profileErrors(form: { name: string; phone: string }): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!form.name.trim()) errors['name'] = i18n.t('settingsPages.company.profile.nameRequired');
  else if (form.name.trim().length > 120)
    errors['name'] = i18n.t('settingsPages.company.profile.atMost120');
  if (form.phone.trim().length > 40)
    errors['phone'] = i18n.t('settingsPages.company.profile.atMost40');
  return errors;
}

/** The sign-in provider as people know it: its host name. */
export function providerName(issuer: string | undefined): string {
  if (!issuer) return i18n.t('settingsPages.company.profile.yourProvider');
  try {
    return new URL(issuer).host;
  } catch {
    return issuer;
  }
}

/** A role's name (ROLE_LABELS keys) in the reader's language. */
function roleLabel(role: string): string {
  return role in ROLE_LABELS
    ? i18n.t(`settingsPages.company.profile.roles.${role as 'outss_admin'}`)
    : role;
}

interface Session {
  signedInAt: string | null;
  expiresAt: string | null;
}

/** Profile and security (document 06 G14): name and phone, the sign-in, and the clients open to me. */
function ProfileAndSecurity() {
  const me = useMe();
  const isDemo = useIsDemo();
  const clients = useClients();
  const update = useUpdateMe();
  const [form, setForm] = useState({ name: '', phone: '' });
  const [language, setLanguageDraft] = useState<'en' | 'ar'>('en');
  const t = useT();
  const [session, setSession] = useState<Session>({ signedInAt: null, expiresAt: null });
  const issuer = auth.manager?.settings.authority;

  useEffect(() => {
    let live = true;
    void auth.getUser().then((u) => {
      if (!live || !u) return;
      const at = typeof u.profile.auth_time === 'number' ? u.profile.auth_time : null;
      setSession({
        signedInAt: at ? new Date(at * 1000).toISOString() : null,
        expiresAt: u.expires_at ? new Date(u.expires_at * 1000).toISOString() : null,
      });
    });
    return () => {
      live = false;
    };
  }, []);

  if (me.isPending) return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  if (me.isError || !me.data)
    return (
      <>
        <PageHeader title={t('settingsPages.company.profile.title')} />
        <EmptyState
          title={t('settingsPages.company.profile.loadError')}
          hint={me.isError ? me.error.message : t('settingsPages.company.profile.signInToSee')}
        />
      </>
    );
  const user = me.data;
  const staffRoles = user.roles.filter((r) => r in ROLE_LABELS);

  return (
    <>
      <PageHeader
        title={t('settingsPages.company.profile.title')}
        description={
          isDemo
            ? t('settingsPages.company.profile.demoDescription')
            : t('settingsPages.company.profile.description')
        }
      />
      <div className="grid gap-4 xl:grid-cols-2">
        <SettingsSection
          title={t('settingsPages.company.profile.section')}
          testId="profile-section"
          canEdit={!isDemo}
          rows={[
            [t('settingsPages.company.profile.name'), user.name],
            [t('settingsPages.company.profile.email'), user.email],
            [t('settingsPages.company.profile.phone'), user.phone ?? ''],
            [
              t('settingsPages.company.profile.role'),
              staffRoles.length ? (
                <span className="flex flex-wrap gap-1">
                  {staffRoles.map((r) => (
                    <Badge key={r} variant="secondary">
                      {roleLabel(r)}
                    </Badge>
                  ))}
                </span>
              ) : (
                t('settingsPages.company.profile.clientUser')
              ),
            ],
          ]}
          onEdit={() => setForm({ name: user.name, phone: user.phone ?? '' })}
          onSave={async () => {
            const errors = profileErrors(form);
            if (Object.keys(errors).length) return errors;
            await update.mutateAsync({ name: form.name.trim(), phone: form.phone.trim() || null });
            return undefined;
          }}
        >
          {(errors) => (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('settingsPages.company.profile.name')} error={errors['name']}>
                {(id, describedBy) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    aria-invalid={Boolean(errors['name'])}
                    value={form.name}
                    maxLength={120}
                    autoComplete="name"
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  />
                )}
              </Field>
              <Field
                label={t('settingsPages.company.profile.phone')}
                error={errors['phone']}
                hint={t('common.optional')}
              >
                {(id, describedBy) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    aria-invalid={Boolean(errors['phone'])}
                    value={form.phone}
                    maxLength={40}
                    autoComplete="tel"
                    inputMode="tel"
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  />
                )}
              </Field>
              <Field
                label={t('settingsPages.company.profile.email')}
                hint={t('settingsPages.company.profile.emailHint')}
              >
                {(id) => <Input id={id} value={user.email} readOnly disabled />}
              </Field>
            </div>
          )}
        </SettingsSection>
        {/* Language (document 15, D2; ADR-0026): each person's own, always offered */}
        <SettingsSection
          title={t('profile.language')}
          testId="language-section"
          canEdit
          rows={[[t('profile.language'), t(`language.${user.language ?? 'en'}`)]]}
          onEdit={() => setLanguageDraft(user.language ?? 'en')}
          onSave={async () => {
            if (!isDemo) await update.mutateAsync({ language });
            await setLanguage(language);
            toast(t('profile.languageSaved'));
            return undefined;
          }}
        >
          {() => (
            <Field label={t('profile.language')} hint={t('profile.languageHint')}>
              {(id) => (
                <NativeSelect
                  id={id}
                  value={language}
                  onChange={(e) => setLanguageDraft(e.target.value as 'en' | 'ar')}
                >
                  <option value="en">{t('language.en')}</option>
                  <option value="ar" lang="ar">
                    {t('language.ar')}
                  </option>
                </NativeSelect>
              )}
            </Field>
          )}
        </SettingsSection>

        <section className="rounded-lg border bg-card" data-testid="security-section">
          <div className="px-5 pt-4">
            <h2 className="text-base font-semibold">
              {t('settingsPages.company.profile.security')}
            </h2>
          </div>
          <dl className="grid grid-cols-[220px_1fr] gap-x-6 gap-y-2 px-5 pt-3 pb-4 text-sm">
            <dt className="text-muted-foreground">
              {t('settingsPages.company.profile.signedInAs')}
            </dt>
            <dd>{user.email}</dd>
            <dt className="text-muted-foreground">{t('settingsPages.company.profile.provider')}</dt>
            <dd>
              {auth.enabled ? providerName(issuer) : t('settingsPages.company.profile.signInOff')}
            </dd>
            <dt className="text-muted-foreground">{t('settingsPages.company.profile.signedIn')}</dt>
            <dd>{session.signedInAt ? formatDateTime(session.signedInAt) : '—'}</dd>
            <dt className="text-muted-foreground">
              {t('settingsPages.company.profile.renewsUntil')}
            </dt>
            <dd>{session.expiresAt ? formatDateTime(session.expiresAt) : '—'}</dd>
            <dt className="text-muted-foreground">{t('settingsPages.company.profile.password')}</dt>
            <dd>
              {auth.accountUrl
                ? t('settingsPages.company.profile.managedOn', { provider: providerName(issuer) })
                : t('settingsPages.company.profile.managedBy', { provider: providerName(issuer) })}
            </dd>
          </dl>
          <div className="flex flex-wrap gap-2 px-5 pb-5">
            {auth.accountUrl ? (
              <Button asChild variant="outline" className="gap-2">
                <a href={auth.accountUrl} target="_blank" rel="noreferrer">
                  <ExternalLink className="size-4" />{' '}
                  {t('settingsPages.company.profile.changePassword')}
                </a>
              </Button>
            ) : null}
            {auth.enabled ? (
              <Button
                type="button"
                variant="outline"
                className="gap-2"
                onClick={() => void auth.signOut()}
              >
                <LogOut className="size-4" /> {t('settingsPages.company.profile.signOut')}
              </Button>
            ) : null}
          </div>
        </section>

        <section className="rounded-lg border bg-card xl:col-span-2" data-testid="access-section">
          <div className="flex items-start justify-between gap-4 px-5 pt-4">
            <div>
              <h2 className="text-base font-semibold">
                {t('settingsPages.company.profile.access')}
              </h2>
              <p className="text-sm text-muted-foreground">
                {t('settingsPages.company.profile.accessHint')}
              </p>
            </div>
            <Link to="/settings/notifications" className="text-sm text-primary">
              {t('settingsPages.company.profile.notifications')}
            </Link>
          </div>
          {clients.isPending ? (
            <p className="px-5 pb-4 text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : !clients.data?.length ? (
            <p className="px-5 pb-4 text-sm text-muted-foreground">
              {t('settingsPages.company.profile.noClients')}
            </p>
          ) : (
            <Table className="mt-2">
              <TableHeader>
                <TableRow>
                  <TableHead>{t('settingsPages.company.profile.client')}</TableHead>
                  <TableHead>{t('settingsPages.company.profile.role')}</TableHead>
                  <TableHead>{t('settingsPages.company.profile.accountants')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clients.data.map((c) => (
                  <TableRow key={c.id} data-testid="access-row">
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell>{roleLabel(c.role)}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {c.accountants.join(', ') || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </section>
      </div>
    </>
  );
}
